'use client'

import React, { createContext, useCallback, useContext, useEffect, useRef, useState, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import { useStickyStackTop } from './stickyStack'
import { Maximize2, Minimize2, MoreVertical, RefreshCw } from 'lucide-react'
import type { ProcessedTheme } from './ProcessedEntry/DomainEntry/processedTypes'
import { AUTO_REFRESH_OPTIONS, formatRelativeUpdated, useAutoRefresh } from './autoRefresh'
import { PrivacyContext, PrivateColumnToggle, PrivateTd, usePrivateColumns } from './privateColumns'

// Sentinel activeFilters value meaning "the user explicitly deselected every
// option" (show nothing), distinct from an empty array (no filter applied,
// show everything) — see FilterDropdown's own "(Select All)" checkbox for
// why these two need to be distinguishable. Never a real filterOptions
// value (a NUL-prefixed string can't come from real column data), so every
// existing consumer of activeFilters — client-side .includes() checks, and
// the SIBS RPC's own = ANY(array) filters — already treats it correctly as
// "matches nothing" with zero changes on their end. The one exception is a
// numeric-typed filter (e.g. platform id) that parses activeFilters entries
// with parseInt before sending them on — that consumer needs to special-case
// this sentinel itself rather than trying to parseInt() it (see
// platformFilterToQueryParams in SibsMailView.tsx for that one).
export const FILTER_NONE_SELECTED = ' __filter_none_selected__'

export type ColumnDef = {
  key: string
  label: React.ReactNode
  /** Starting width in px. Omit to let the browser size the column initially. */
  initialWidth?: number
  /** Minimum width in px during drag. Default 40. */
  minWidth?: number
  /** Extra classes on the <th> element (e.g. "w-7", "min-w-[280px]"). */
  thClassName?: string
  /** Tooltip shown on the <th> element. */
  thTitle?: string
  /** Distinct values shown in the Excel-like filter dropdown. */
  filterOptions?: string[]
  /** Currently selected filter values. Empty array = no filter (show all); [FILTER_NONE_SELECTED] = explicitly show nothing. */
  activeFilters?: string[]
  /** Called when the user changes the filter selection. */
  onFilterChange?: (values: string[]) => void
  /** Display label for a filterOptions value, when the value itself isn't human-readable (e.g. a platform id needing its catalog name). Defaults to the raw value. */
  filterOptionLabel?: (value: string) => string
  /** Pin this column's <th> to the left edge while the table pans
   * horizontally (see PanContext below). Meant for a single leading column
   * (e.g. the checkbox column) — offsets for a second sticky column aren't
   * implemented since nothing in the app needs more than one yet. */
  stickyLeft?: boolean
  /** Blur this column while the sidebar's demo mask is on. Defaults to the
   * key: beneficiary, platform, source, brand (see privateColumns.tsx). */
  private?: boolean
}

type ResizeSession = { colIdx: number; startX: number; startWidths: number[] }

// Horizontal panning here is a `transform: translateX()` on the whole
// <table>, not a real scrollLeft (see the "Horizontal scroll failsafe"
// comment on ResizableTable below for why) — so native `position: sticky;
// left: 0` can't work on a body cell, since nothing ever actually scrolls
// for it to react to. A sticky-left cell instead counters the table's own
// pan with an equal and opposite translateX of its own, which fully cancels
// out and leaves it visually fixed regardless of the caller's pan position.
// ResizableTable provides the live pan distance here so TableTd/TableCheckboxTd
// (rendered by callers, not by ResizableTable itself, since rows are passed
// in as `children`) can apply that counter-transform via their own `sticky`
// prop without every caller having to know panX exists.
const PanContext = createContext(0)

// Standard <td> companion for ResizableTable — was duplicated identically in
// SibsMailView.tsx and BackofficeApp.tsx as a local `CampTd`; every entries
// table in the app (SIBS's Custom Mail Alert, CTT's Alertas Phishing/Custom
// Mail Alert/Standard Entries, Publicadas, Novas Entradas, Email Logs,
// Tenant Logs, and the small in-widget log previews in SibsWidgets.tsx)
// uses this one instead now.
export function TableTd({
  children,
  className = '',
  onClick,
  dense = false,
  title,
  sticky = false,
}: {
  children: React.ReactNode
  className?: string
  onClick?: React.MouseEventHandler<HTMLTableCellElement>
  /** Tighter padding (px-2 py-1) for small in-widget tables (e.g. the log
   * previews embedded in SibsWidgets.tsx cards) where the default px-3 py-2
   * would eat too much of a card's limited height. */
  dense?: boolean
  title?: string
  /** Pin this cell to the left edge while the table pans horizontally — the
   * body-cell half of ColumnDef's `stickyLeft` (see PanContext above); pass
   * this on the same column's cells the header marked `stickyLeft: true`.
   * `bg-inherit` picks up whatever background the row's own <tr> currently
   * has (hover/selected/flagged states included, since CSS inheritance of
   * background-color re-evaluates live) so the pinned cell doesn't go
   * transparent and let panned-away columns show through underneath it. */
  sticky?: boolean
}) {
  const panX = useContext(PanContext)
  // overflow-hidden unconditionally — a cell's content (a fixed-width badge,
  // an unbroken long string, …) must never be able to visually bleed past
  // its own column into the next one just because a caller forgot to pass
  // truncate/line-clamp on its own children. Wrapping text still wraps
  // (increasing row height) same as before; this only clips the axis a
  // column's resizable width actually constrains. A PrivateTd: in a private
  // column it blurs while the demo mask is on (privateColumns.tsx).
  return (
    <PrivateTd
      className={`${dense ? 'px-2 py-1' : 'px-3 py-2'} align-middle overflow-hidden ${sticky ? 'relative z-10 bg-inherit' : ''} ${className}`}
      onClick={onClick}
      title={title}
      style={sticky ? { transform: `translateX(${panX}px)` } : undefined}
    >
      {children}
    </PrivateTd>
  )
}

// Table-wide convention for any table that pairs a checkbox column with
// click-anywhere-on-the-row-to-open: once at least one row is already
// checked, a row's onClick should call `toggle` instead of `open`, not just
// the checkbox's own onChange. Without this, a misclick that lands next to
// the (necessarily small) checkbox hit target while multi-selecting opens
// that row's detail view instead of adding it to the selection — every row
// stays a full-size toggle target as soon as selection is in progress,
// rather than only ever the checkbox itself. Every table with a checkbox
// column should route its row onClick through this rather than reimplementing
// the hasSelection check inline, so the behavior stays identical everywhere
// it's used (currently CampaignsView's entries table and AlertsAnalysisView's
// EmailsSentTable, both in SibsMailView.tsx).
export function rowClickToggleOrOpen(hasSelection: boolean, toggle: () => void, open: () => void) {
  if (hasSelection) toggle()
  else open()
}

// A checkbox column reused an existing narrow utility column (e.g.
// sibsColumns' own 32px 'expand' column, meant for an empty label) in more
// than one table — 32px minus the <th>/<td> default px-3 padding (24px)
// leaves just 8px for the checkbox itself, clipping it visibly. Every
// checkbox column's ColumnDef should set both initialWidth and thClassName
// (as `w-11`) to this, and every row cell should size to match, so the
// column is never narrower than the padding it's rendered inside of.
export const CHECKBOX_COL_WIDTH = 44
export const CHECKBOX_COL_TH_CLASS = 'w-11'

// The other half of the checkbox-column convention above: the checkbox
// cell itself should always be a full-cell toggle target, not just the
// ~14px input — that's true even on the very first row checked, before
// rowClickToggleOrOpen's "whole row toggles once selection exists" kicks
// in. pointer-events-none hands every mouse click anywhere in the cell to
// the TD's own onClick (the single source of truth for the toggle here, so
// there's no risk of it double-firing against the input's own onChange);
// onChange is kept on the input so keyboard activation (Tab + Space, which
// pointer-events doesn't affect) still works. Every table with a checkbox
// column should render its cells through this rather than hand-rolling the
// pointer-events/onClick pairing per table.
export function TableCheckboxTd({
  checked,
  onToggle,
  disabled,
  title,
  className = CHECKBOX_COL_TH_CLASS,
  sticky = false,
}: {
  checked: boolean
  onToggle: () => void
  disabled?: boolean
  title?: string
  className?: string
  /** See TableTd's own `sticky` — pass true when this checkbox column is
   * also marked `stickyLeft: true` on its ColumnDef. */
  sticky?: boolean
}) {
  return (
    <TableTd
      onClick={(ev) => { if (disabled) return; ev.stopPropagation(); onToggle() }}
      className={`${disabled ? '' : 'cursor-pointer'} ${className}`}
      title={title}
      sticky={sticky}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onToggle}
        className={`pointer-events-none ${disabled ? 'cursor-not-allowed opacity-30' : 'cursor-pointer'}`}
      />
    </TableTd>
  )
}

// Same full-cell-toggle idea for the header's own "select all" checkbox —
// pass this as a checkbox ColumnDef's `label`. The <th> block below already
// wraps col.label in its own padded flex row, so this only needs to fill
// that row's height, not the whole <th>, for the click target to cover
// everything the row equivalent does.
export function TableCheckboxHeaderLabel({
  checked,
  onToggle,
  title = 'Select all',
}: {
  checked: boolean
  onToggle: () => void
  title?: string
}) {
  return (
    <span
      onClick={(ev) => { ev.stopPropagation(); onToggle() }}
      className="flex h-full w-full cursor-pointer items-center"
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        title={title}
        className="pointer-events-none cursor-pointer"
      />
    </span>
  )
}

// ─── Inline funnel icon ───────────────────────────────────────────────────────

function FunnelIcon({ filled }: { filled?: boolean }) {
  return (
    <svg
      width="10" height="10" viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round"
    >
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  )
}

// ─── Filter dropdown ──────────────────────────────────────────────────────────

function FilterDropdown({
  options,
  active,
  onChange,
  onClose,
  style,
  dropdownRef,
  optionLabel,
  blurred = false,
}: {
  options: string[]
  active: string[]
  onChange?: (values: string[]) => void
  onClose: () => void
  /** Viewport position (position: fixed) — see the portal comment at this component's call site for why this isn't just CSS-anchored to the trigger. */
  style: React.CSSProperties
  /** Excluded from the "click outside closes it" check the same way the trigger button already is — needed because a portaled node isn't a DOM descendant of the trigger's own container span anymore. */
  dropdownRef: React.Ref<HTMLDivElement>
  /** See ColumnDef's own filterOptionLabel doc. */
  optionLabel?: (value: string) => string
  /** The column is private and hidden: the values are names, so they blur too. */
  blurred?: boolean
}) {
  // Local draft, only committed to the real activeFilters (which is what
  // actually triggers a fetch/query — see e.g. fetchEntries' useEffect deps
  // on platformFilter/sourceTypeFilter) via the explicit Apply button below.
  // This dropdown only ever mounts while open (see the call site's
  // `openFilterKey === col.key && ...` guard) and unmounts on close, so
  // seeding useState directly from the `active` prop is exactly "start each
  // opening from whatever's currently applied" with no separate sync effect
  // needed — closing without Apply (outside-click, Escape-equivalent
  // Cancel) just discards this instance's state, same as never having
  // called onChange at all.
  const [pending, setPending] = useState<string[]>(active)
  // Only shown once there's enough options that scanning for one by eye
  // stops being faster than typing a few letters — narrows which checkboxes
  // render below, not which ones "(Select All)" acts on (that always stays
  // scoped to every option, matching/full-none, regardless of search — see
  // FILTER_NONE_SELECTED's own doc for why that scope can't shrink to just
  // the visible subset without breaking the empty-array-means-all encoding).
  const [search, setSearch] = useState('')
  const SEARCH_THRESHOLD = 6
  const matches = (opt: string) => {
    const label = (optionLabel ? optionLabel(opt) : opt) || '(empty)'
    return label.toLowerCase().includes(search.trim().toLowerCase())
  }
  const visibleOptions = options.length > SEARCH_THRESHOLD && search.trim()
    ? options.filter(matches)
    : options

  const allSelected = pending.length === 0
  const noneSelected = pending.length === 1 && pending[0] === FILTER_NONE_SELECTED

  const isChecked = (value: string) => allSelected || (!noneSelected && pending.includes(value))

  const toggle = (value: string) => {
    if (allSelected) {
      setPending(options.filter((o) => o !== value))
    } else if (noneSelected) {
      // Starting from explicit-empty, not "all minus everything else" —
      // checking one value here means exactly that one, not options.length-1.
      setPending([value])
    } else if (pending.includes(value)) {
      const next = pending.filter((v) => v !== value)
      // Unchecking the last remaining value used to silently fall back to []
      // (no filter, i.e. show everything) instead of staying at "explicitly
      // nothing selected" — the FILTER_NONE_SELECTED sentinel here is what
      // makes that an actually reachable, distinct state.
      setPending(next.length === 0 ? [FILTER_NONE_SELECTED] : next)
    } else {
      const next = [...pending, value]
      setPending(next.length === options.length ? [] : next)
    }
  }

  const dirty = pending.length !== active.length || pending.some((v, i) => v !== active[i])

  return (
    <div ref={dropdownRef} style={style} className="fixed z-50 flex flex-col min-w-[160px] max-h-80 bg-card border border-border shadow-notice rounded-[4px] text-foreground">
      {options.length > SEARCH_THRESHOLD && (
        <input
          type="text"
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.stopPropagation()}
          placeholder="Search…"
          className="border-b border-border bg-transparent px-3 py-1.5 font-mono text-small text-foreground outline-none focus:bg-secondary"
        />
      )}
      <div className="max-h-64 overflow-y-auto py-1">
        {!search.trim() && (
          <>
            <label className="flex items-center gap-2 px-3 py-1.5 hover:bg-secondary cursor-pointer select-none">
              <input
                type="checkbox"
                checked={allSelected}
                // Toggles between "everything selected" (the usual no-filter
                // default) and "nothing selected" — previously this always
                // reset to all-selected regardless of the current state, so
                // there was no way to reach "show nothing" from here at all.
                onChange={() => setPending(allSelected ? [FILTER_NONE_SELECTED] : [])}
                className="cursor-pointer"
              />
              <span className="font-mono text-label uppercase tracking-widest text-muted-foreground font-semibold">
                (Select All)
              </span>
            </label>
            <div className="h-px bg-border my-0.5" />
          </>
        )}
        {visibleOptions.length === 0 && (
          <div className="px-3 py-1.5 font-mono text-small text-subtle">No matches</div>
        )}
        {visibleOptions.map((opt) => (
          <label
            key={opt}
            className="flex items-center gap-2 px-3 py-1.5 hover:bg-secondary cursor-pointer select-none"
          >
            <input
              type="checkbox"
              checked={isChecked(opt)}
              onChange={() => toggle(opt)}
              className="cursor-pointer"
            />
            <span className={`font-mono text-small text-foreground truncate ${blurred ? 'select-none blur-[4px]' : ''}`}>{(optionLabel ? optionLabel(opt) : opt) || '(empty)'}</span>
          </label>
        ))}
      </div>
      {/* Nothing above calls onChange — every checkbox click here only
          updates the local draft, so picking several values in a row (the
          normal case) doesn't fire a fetch per click. Apply is the one place
          that actually commits, Cancel/outside-click discards. */}
      <div className="flex border-t border-border">
        {!allSelected && (
          <button
            onClick={() => { onChange?.([]); onClose() }}
            className="flex-1 px-3 py-1.5 text-left font-mono text-label uppercase tracking-widest text-muted-foreground hover:bg-secondary"
          >
            Clear
          </button>
        )}
        <button
          onClick={onClose}
          className="flex-1 px-3 py-1.5 text-left font-mono text-label uppercase tracking-widest text-muted-foreground hover:bg-secondary"
        >
          Cancel
        </button>
        <button
          onClick={() => { onChange?.(pending); onClose() }}
          disabled={!dirty}
          className="flex-1 px-3 py-1.5 text-left font-mono text-label uppercase tracking-widest text-signal hover:bg-signal-surface disabled:opacity-40 disabled:hover:bg-transparent"
        >
          Apply
        </button>
      </div>
    </div>
  )
}

// ─── "..." auto-refresh menu ───────────────────────────────────────────────────

// Same "updated Xm ago" + configurable auto-refresh interval as
// BaseDashboard's header RefreshControl (see autoRefresh.ts), but tucked
// behind a "..." button in the table's own control bar instead of an
// always-visible header cluster — every ResizableTable gets this for free
// just by being passed onRefresh, regardless of whether it's wrapped in a
// BaseDashboard at all (many callers, e.g. the Entries views and SIBS
// widgets' log-preview tables, render this table standalone).
function MoreMenu({
  theme,
  lastUpdated,
  onRefresh,
  refreshing,
  storageKey,
}: {
  theme: ProcessedTheme
  lastUpdated?: number | null
  onRefresh: () => void
  refreshing?: boolean
  storageKey?: string
}) {
  const isDark = theme === 'dark'
  const { nowTick, intervalMs, setIntervalMs } = useAutoRefresh({ lastUpdated, onRefresh, storageKey })
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  return (
    <div className="relative shrink-0" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Auto-refresh"
        className={`flex items-center justify-center h-5 w-5 rounded transition-colors ${
          open || intervalMs ? 'text-signal' : 'text-subtle hover:text-signal'
        }`}
      >
        <MoreVertical className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div
          className="absolute top-full right-0 z-30 mt-2 w-48 rounded-[4px] border border-border bg-card p-3 shadow-notice"
        >
          {lastUpdated != null && (
            <div
              title={new Date(lastUpdated).toLocaleString()}
              className="font-mono text-label uppercase tracking-widest mb-2 text-subtle"
            >
              updated {formatRelativeUpdated(lastUpdated, nowTick)}
            </div>
          )}
          <label className="block font-mono text-micro uppercase tracking-widest mb-1 text-subtle">
            Auto-refresh
          </label>
          <select
            value={intervalMs ?? ''}
            onChange={(e) => setIntervalMs(e.target.value ? Number(e.target.value) : null)}
            className="w-full h-7 border border-border bg-transparent font-mono text-small px-1.5 mb-2 text-foreground"
          >
            {AUTO_REFRESH_OPTIONS.map((opt) => (
              <option key={opt.label} value={opt.ms ?? ''}>{opt.label}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={onRefresh}
            disabled={refreshing}
            className="w-full flex items-center justify-center gap-1.5 h-7 border border-border bg-card font-mono text-label uppercase tracking-widest text-muted-foreground transition-colors hover:text-signal hover:border-signal disabled:opacity-50"
          >
            <RefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh now
          </button>
        </div>
      )}
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export function ResizableTable({
  columns,
  children,
  stickyHeader = false,
  /** Always apply table-fixed layout (even before a drag sets explicit widths). */
  fixedLayout = false,
  /** Add overflow:hidden to every td so content never bleeds into the next cell. */
  clipCells = false,
  /** className applied to the <table>. Default: "w-full text-[12px]" */
  tableClassName = 'w-full text-[12px]',
  /** className applied to the header <tr>. Default: main-table style. */
  thRowClassName = 'font-mono text-micro uppercase tracking-widest text-muted-foreground',
  /** Extra className on <thead>. Sticky bg is added automatically when stickyHeader=true. */
  theadClassName = '',
  /** Default padding on each <th>. Default: "px-3 py-2". */
  thPadding = 'px-3 py-2',
  isEmpty = false,
  emptyMessage = 'No entries found',
  /** Show a maximize button that expands the table into a full-viewport overlay — useful for wide tables that are otherwise cramped, especially on mobile/tablet. */
  maximizable = true,
  /** Only affects the maximized-overlay's own background/close-button colors — every other style stays under the existing className props, unrelated to this. */
  theme = 'light',
  /** Refetch callback for both the "..." menu's manual "Refresh now" button
   * and its auto-refresh interval timer (see autoRefresh.ts). Omit to hide
   * the "..." button entirely — there's nothing for it to do without one. */
  onRefresh,
  refreshing,
  /** Timestamp (Date.now()) of the last successful load — shown as "updated
   * Xm ago" inside the "..." menu. Purely cosmetic without onRefresh. */
  lastUpdated,
  /** Namespaces the auto-refresh interval selection in localStorage (see
   * autoRefresh.ts) so it's restored on reload instead of always starting
   * back at "Auto off". Omit to leave the interval unpersisted. */
  storageKey,
  /** Search/filter/count controls (whatever a caller used to render in its
   * own toolbar div right above this table) — rendered as the left side of
   * this table's own single header bar, with "..."/maximize pushed to the
   * far right of that same bar, instead of the caller's toolbar and this
   * table's controls stacking as two separate bars. */
  toolbar,
  /** Height (px) of whatever sticky content the caller already stacks above
   * this table (e.g. a search/bulk-action bar) — added to `top` on both
   * this table's own topBar and its sticky column headers so the two stay
   * pinned directly below the caller's bar instead of underneath it.
   * Irrelevant unless stickyHeader is also set. */
  stickyOffset = 0,
  /** Cap the visible body at this many rows. The table then scrolls inside
   * its own box (the viewport becomes a real vertical scroller, as in
   * maximized mode) with the column headers pinned to that box's top edge;
   * fewer rows than the cap and nothing scrolls. The box height is measured
   * from the real header and first rows, so wrapped cells count. */
  maxRows,
}: {
  columns: ColumnDef[]
  children: React.ReactNode
  stickyHeader?: boolean
  fixedLayout?: boolean
  clipCells?: boolean
  tableClassName?: string
  thRowClassName?: string
  theadClassName?: string
  thPadding?: string
  isEmpty?: boolean
  emptyMessage?: string
  maximizable?: boolean
  theme?: ProcessedTheme
  onRefresh?: () => void
  refreshing?: boolean
  lastUpdated?: number | null
  storageKey?: string
  toolbar?: React.ReactNode
  stickyOffset?: number
  maxRows?: number
}) {
  const [widths, setWidths] = useState<(number | null)[]>(() =>
    columns.map((c) => c.initialWidth ?? null),
  )
  const [maximized, setMaximized] = useState(false)
  const tableRef = useRef<HTMLTableElement>(null)
  const session = useRef<ResizeSession | null>(null)
  // Which columns blur while the demo mask is on (privateColumns.tsx).
  const privacy = usePrivateColumns(columns)

  // Filter dropdown state
  const [openFilterKey, setOpenFilterKey] = useState<string | null>(null)
  const filterContainerRefs = useRef<Map<string, HTMLSpanElement | null>>(new Map())
  // Position (viewport coords, for the portaled dropdown below) computed
  // from the trigger button's own bounding rect at open time.
  const [filterDropdownPos, setFilterDropdownPos] = useState<{ top: number; left: number } | null>(null)
  const filterDropdownRef = useRef<HTMLDivElement | null>(null)

  // ── Sticky-header stacking ─────────────────────────────────────────────────
  // topBar (built further down, holds `toolbar` + the maximize/"..." buttons)
  // sticks right below stickyOffset (the caller's own sticky bar, if any);
  // the column headers then stick below topBar in turn. Measured rather than
  // assumed fixed-height since `toolbar` is caller content of arbitrary size.
  const topBarRef = useRef<HTMLDivElement>(null)
  const [topBarHeight, setTopBarHeight] = useState(0)
  // Plus whatever an ancestor already pins at the top (BaseDashboard's title
  // bar) -- see stickyStack.ts; without it the headers stuck underneath it.
  const stackTop = useStickyStackTop()
  const stickyBase = stickyOffset + stackTop
  useEffect(() => {
    const el = topBarRef.current
    if (!el) { setTopBarHeight(0); return }
    const ro = new ResizeObserver(() => setTopBarHeight(el.offsetHeight))
    ro.observe(el)
    setTopBarHeight(el.offsetHeight)
    return () => ro.disconnect()
  }, [toolbar, onRefresh, maximizable])

  // ── Row cap (maxRows) ─────────────────────────────────────────────────────
  // With maxRows the viewport is this table's own scroller, like maximized
  // mode: the page's sticky stacking (stickyBase/topBarHeight) no longer
  // applies, the headers pin at the box's top edge and topBar just sits
  // above the box. boxHeight is header + the first maxRows rows, measured
  // (rows wrap to different heights) and re-measured when they change size;
  // null while there is nothing to cap so a short table is not boxed.
  const ownScroller = maxRows != null
  const [boxHeight, setBoxHeight] = useState<number | null>(null)
  useLayoutEffect(() => {
    const table = tableRef.current
    if (!ownScroller || maximized || !table) { setBoxHeight(null); return }
    const measure = () => {
      const head = table.querySelector(':scope > thead') as HTMLElement | null
      const rows = Array.from(table.querySelectorAll(':scope > tbody > tr')) as HTMLElement[]
      if (rows.length <= maxRows) { setBoxHeight(null); return }
      const h = (head?.offsetHeight ?? 0) + rows.slice(0, maxRows).reduce((a, r) => a + r.offsetHeight, 0)
      setBoxHeight((prev) => (prev === h ? prev : h))
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(table)
    return () => ro.disconnect()
  }, [ownScroller, maxRows, maximized, children])

  // ── Resize drag ───────────────────────────────────────────────────────────

  const startResize = useCallback((e: React.MouseEvent, idx: number) => {
    e.preventDefault()
    e.stopPropagation()
    const ths = Array.from(
      tableRef.current?.querySelectorAll(':scope > thead > tr > th') ?? [],
    ) as HTMLElement[]
    const startWidths = ths.map((th) => th.offsetWidth)
    session.current = { colIdx: idx, startX: e.clientX, startWidths }
    setWidths(startWidths)
  }, [])

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!session.current) return
      const { colIdx, startX, startWidths } = session.current
      const minW = columns[colIdx]?.minWidth ?? 40
      const next = Math.max(minW, startWidths[colIdx] + (e.clientX - startX))
      setWidths((prev) => {
        const arr = [...prev] as number[]
        arr[colIdx] = next
        return arr
      })
    }
    const onUp = () => { session.current = null }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [columns])

  // ── Horizontal scroll failsafe ────────────────────────────────────────────
  // Several callers (e.g. SIBS entries) deliberately wrap this table in an
  // `overflow-clip` card instead of `overflow-hidden`/`overflow-auto`, so
  // that a sticky <th> escapes to the page's own scrolling ancestor instead
  // of getting trapped by a local scroll container (see SibsMailView.tsx's
  // own comment on that card). The catch: `overflow: clip` isn't merely a
  // hidden scrollbar the way `hidden` is — per spec it's genuinely not a
  // scroll container at all, so `element.scrollLeft = x` silently does
  // nothing there, and neither does a native touch-scroll gesture. Content
  // past the clip edge is just gone, not scrollable by any means.
  //
  // So instead of hunting for a real scrollable ancestor (which may not
  // exist), this owns a local `overflow-clip` viewport around the table and
  // pans its contents with a CSS `transform: translateX()` — clip still
  // crops a transformed child exactly like it would a scrolled one, but
  // moving it doesn't require the box to be a scroll container, so it works
  // uniformly regardless of whatever the caller's ancestor chain looks
  // like. Driven by the slider, a direct touch/pointer drag on the table
  // itself (touch-action: pan-y hands vertical drags to the page but keeps
  // horizontal ones in our own JS, sidestepping iOS's edge-swipe-back
  // gesture instead of trying to out-CSS it), and a trackpad's horizontal
  // wheel delta.
  const viewportRef = useRef<HTMLDivElement>(null)
  const [panX, setPanX] = useState(0)
  const [maxPan, setMaxPan] = useState(0)
  const [viewportWidth, setViewportWidth] = useState(0)
  const dragRef = useRef<{ startX: number; startPan: number } | null>(null)

  const measurePan = useCallback(() => {
    const table = tableRef.current
    const viewport = viewportRef.current
    if (!table || !viewport) return
    const max = Math.max(0, table.scrollWidth - viewport.clientWidth)
    setMaxPan(max)
    setViewportWidth(viewport.clientWidth)
    setPanX((prev) => Math.min(prev, max))
  }, [])

  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return
    measurePan()
    const ro = new ResizeObserver(measurePan)
    ro.observe(viewport)
    window.addEventListener('resize', measurePan)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', measurePan)
    }
  }, [measurePan, widths, maximized])

  const onPanPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'mouse' || maxPan <= 0) return
    if ((e.target as HTMLElement).closest('button, input, a, .cursor-col-resize')) return
    dragRef.current = { startX: e.clientX, startPan: panX }
    e.currentTarget.setPointerCapture(e.pointerId)
  }, [panX, maxPan])

  const onPanPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return
    e.preventDefault()
    const delta = dragRef.current.startX - e.clientX
    setPanX(Math.min(maxPan, Math.max(0, dragRef.current.startPan + delta)))
  }, [maxPan])

  const onPanPointerEnd = useCallback(() => { dragRef.current = null }, [])

  const onPanWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    if (maxPan <= 0 || Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return
    e.preventDefault()
    setPanX((prev) => Math.min(maxPan, Math.max(0, prev + e.deltaX)))
  }, [maxPan])

  // ── Filter click-outside ──────────────────────────────────────────────────

  useEffect(() => {
    if (!openFilterKey) return
    const handler = (e: MouseEvent) => {
      const target = e.target as Node
      const ref = filterContainerRefs.current.get(openFilterKey)
      // Portaled into document.body (see the render site below), so it's no
      // longer a DOM descendant of the trigger's own container span —
      // without also checking filterDropdownRef here, every click inside
      // the dropdown itself (e.g. a checkbox) would register as "outside"
      // and close it before the click could register.
      if ((!ref || !ref.contains(target)) && !filterDropdownRef.current?.contains(target)) {
        setOpenFilterKey(null)
      }
    }
    document.addEventListener('mousedown', handler)
    // The dropdown's position is computed once at open time (see the click
    // handler below) rather than tracked live — simplest correct behavior
    // for a scroll/resize mid-open is to just close it instead of drifting
    // out of alignment with its trigger. Capture-phase 'scroll' also sees
    // scrolling *inside* the dropdown's own option list (max-h-64
    // overflow-y-auto) — that one must not close it.
    const closeOnScroll = (e: Event) => {
      if (filterDropdownRef.current?.contains(e.target as Node)) return
      setOpenFilterKey(null)
    }
    window.addEventListener('scroll', closeOnScroll, true)
    window.addEventListener('resize', closeOnScroll)
    return () => {
      document.removeEventListener('mousedown', handler)
      window.removeEventListener('scroll', closeOnScroll, true)
      window.removeEventListener('resize', closeOnScroll)
    }
  }, [openFilterKey])

  // ── Maximize overlay ──────────────────────────────────────────────────────

  useEffect(() => {
    if (!maximized) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMaximized(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [maximized])

  // ── Rendering ─────────────────────────────────────────────────────────────

  const hasFixed = widths.some((w) => w !== null)
  const useFixed = hasFixed || fixedLayout

  // Tailwind's Preflight sets `border-collapse: collapse` on every <table>,
  // which is well known to break `position: sticky` on <thead> (and <tr>) in
  // most browsers — the browser has nowhere consistent to anchor the sticky
  // box once cell borders are collapsed into the table's own border. Sticky
  // on individual <th> cells doesn't have that problem, so that's where
  // stickyHeader applies it below, not on <thead> itself.
  //
  // theadClassName supplies the sticky header's own background/border (it
  // must be opaque to occlude rows scrolling underneath it) — Tailwind class
  // order in a string doesn't reliably decide which bg-* utility wins, so a
  // caller-supplied theadClassName fully replaces the default light-mode bg
  // instead of being merged alongside it.
  // `top` is set inline (not a Tailwind class) since it depends on
  // stickyOffset + the measured topBarHeight, not a fixed value.
  const stickyThClass = stickyHeader
    ? `sticky z-10 ${theadClassName || 'bg-sidebar-surface border-b border-border'}`
    : ''
  // In maximized mode topBar sits in normal flow above the now-really-
  // scrollable viewport (see the maximized branch below) rather than
  // stacking via `top` offsets, so the header only needs to clear its own
  // top edge (0), not stickyOffset/topBarHeight — those describe stacking
  // against the *page's* scroll, which isn't what's scrolling here anymore.
  // Same for a maxRows box: the viewport is the scroller, so 0 there too.
  const stickyThTop = maximized || ownScroller ? 0 : stickyBase + topBarHeight

  const tableClass = [
    tableClassName,
    useFixed ? 'table-fixed' : '',
    clipCells ? '[&_td]:overflow-hidden' : '',
  ]
    .filter(Boolean)
    .join(' ')

  // table-fixed makes the table ignore its content when sizing — combined
  // with the default `w-full`, that locks the table to exactly 100% of its
  // container, so on a narrow viewport it just crushes every column to fit
  // instead of overflowing (nothing left to scroll, defeating both the
  // native swipe and the failsafe bar below). min-width wins over width in
  // CSS, so summing the columns' widths here forces genuine overflow once
  // the container gets narrower than the columns actually need, while
  // leaving wide/desktop layouts untouched (100% still exceeds the sum).
  const fixedMinWidth = useFixed
    ? widths.reduce((sum: number, w, i) => sum + (w ?? columns[i]?.minWidth ?? 80), 0)
    : undefined

  const tableElement = (
    <table
      ref={tableRef}
      className={tableClass}
      style={{
        ...(fixedMinWidth !== undefined ? { minWidth: fixedMinWidth } : {}),
        ...(panX ? { transform: `translateX(-${panX}px)` } : {}),
      }}
    >
      {useFixed && (
        <colgroup>
          {widths.map((w, i) => (
            <col key={i} style={w !== null ? { width: w } : undefined} />
          ))}
        </colgroup>
      )}
      <thead className={theadClassName || undefined}>
        <tr className={thRowClassName}>
          {columns.map((col, idx) => {
            const hasFilter = (col.filterOptions?.length ?? 0) > 0
            const isFilterActive = (col.activeFilters?.length ?? 0) > 0
            // z-20 (above the z-10 plain sticky header) so this column stays
            // on top of the others while they pan away underneath it — see
            // PanContext's own comment above for why a counter-transform,
            // not `left: 0`, is what actually pins it.
            const stickyLeftStyle = col.stickyLeft
              ? { transform: `translateX(${panX}px)`, ...(stickyHeader ? { top: stickyThTop } : {}) }
              : stickyHeader ? { top: stickyThTop } : undefined
            return (
              <th
                key={col.key}
                title={col.thTitle}
                className={`${thPadding} text-left font-normal select-none ${stickyHeader ? stickyThClass : 'relative'} ${col.stickyLeft ? 'z-20 bg-inherit' : ''} ${col.thClassName ?? ''}`}
                style={stickyLeftStyle}
              >
                <span className="flex items-center gap-1 overflow-hidden pr-1">
                  <span className="truncate flex-1 min-w-0">{col.label}</span>
                  <PrivateColumnToggle colIdx={idx} privacy={privacy} />
                  {hasFilter && (
                    <span
                      ref={(el) => { filterContainerRefs.current.set(col.key, el) }}
                      className="relative shrink-0"
                    >
                      <button
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation()
                          // Read eagerly, not inside the updater below —
                          // React nulls out e.currentTarget as soon as this
                          // synchronous handler returns, and a functional
                          // setState updater isn't guaranteed to still be
                          // mid-call at that point.
                          const rect = e.currentTarget.getBoundingClientRect()
                          setOpenFilterKey((prev) => {
                            if (prev === col.key) return null
                            // Viewport-relative, since the dropdown below
                            // renders via a portal at position: fixed — see
                            // its own comment for why plain absolute
                            // positioning inside this table doesn't work.
                            // Clamped so a filter button near the table's
                            // right edge doesn't hang the (min-w-[160px])
                            // dropdown off the edge of the viewport.
                            const left = Math.max(8, Math.min(rect.left, window.innerWidth - 200))
                            setFilterDropdownPos({ top: rect.bottom + 4, left })
                            return col.key
                          })
                        }}
                        className={`flex items-center justify-center h-4 w-4 rounded transition-colors ${
                          isFilterActive
                            ? 'text-signal hover:text-signal/80'
                            : 'text-subtle/60 hover:text-subtle'
                        }`}
                        title={isFilterActive ? 'Filter active — click to change' : 'Filter column'}
                      >
                        <FunnelIcon filled={isFilterActive} />
                      </button>
                      {openFilterKey === col.key && filterDropdownPos && createPortal(
                        <FilterDropdown
                          options={col.filterOptions!}
                          active={col.activeFilters ?? []}
                          onChange={col.onFilterChange}
                          onClose={() => setOpenFilterKey(null)}
                          style={{ top: filterDropdownPos.top, left: filterDropdownPos.left }}
                          dropdownRef={filterDropdownRef}
                          optionLabel={col.filterOptionLabel}
                          blurred={privacy.colHidden(idx)}
                        />,
                        document.body,
                      )}
                    </span>
                  )}
                </span>
                {/* Drag handle — invisible until hover */}
                <span
                  onMouseDown={(e) => startResize(e, idx)}
                  className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize z-20 flex items-stretch"
                  aria-hidden
                >
                  <span className="w-px bg-transparent hover:bg-signal active:bg-signal transition-colors mx-auto" />
                </span>
              </th>
            )
          })}
        </tr>
      </thead>
      <tbody>
        {isEmpty ? (
          <tr>
            <td
              colSpan={columns.length}
              className="px-3 py-8 text-center font-mono text-small text-subtle uppercase tracking-widest"
            >
              {emptyMessage}
            </td>
          </tr>
        ) : (
          // children are rows rendered by the caller (TableTd/TableCheckboxTd),
          // not by ResizableTable itself — PanContext is how a caller's own
          // `sticky` cell gets the live pan distance without every caller
          // having to thread panX through by hand.
          <PanContext.Provider value={panX}><PrivacyContext.Provider value={privacy}>{children}</PrivacyContext.Provider></PanContext.Provider>
        )}
      </tbody>
    </table>
  )

  const isDark = theme === 'dark'

  // Owns the clipping + panning viewport itself rather than depending on
  // whatever the caller's ancestor chain does — see the "Horizontal scroll
  // failsafe" comment above. `overflow-clip` (both axes, not just x) avoids
  // the CSS quirk where leaving one axis at its default `visible` promotes
  // it to `auto`, which would turn this div into a scroll container and
  // trap the sticky header here instead of the page's real scrolling
  // ancestor. In maximized mode there's no outer page to escape to — the
  // overlay IS the scroll context — so it gets a real overflow-y-auto
  // instead, alongside the same overflow-x-clip for the pan transform.
  const viewportElement = (
    <div
      ref={viewportRef}
      className={`min-w-0 ${maximized || ownScroller ? 'overflow-y-auto overflow-x-clip' : 'overflow-clip'}`}
      style={{ touchAction: 'pan-y', maxHeight: !maximized && boxHeight != null ? boxHeight : undefined }}
      onPointerDown={onPanPointerDown}
      onPointerMove={onPanPointerMove}
      onPointerUp={onPanPointerEnd}
      onPointerCancel={onPanPointerEnd}
      onWheel={onPanWheel}
    >
      {tableElement}
    </div>
  )

  // Independent of `maximizable` — this is a usability failsafe, not the
  // maximize feature, so it still renders for tables that opted out of the
  // maximize button (e.g. small embedded log-preview tables).
  //
  // A thumb-and-track control (like a real scrollbar) rather than a slider
  // knob — thumb width reflects how much of the table is currently visible,
  // its position reflects panX/maxPan. Absolute cursor position is
  // recomputed on every pointerdown/move (via panFromClientX) rather than
  // tracked as a drag delta, so there's no separate drag-session state to
  // manage: setPointerCapture keeps the events coming to this element even
  // once the cursor leaves its bounds, and checking e.buttons on move tells
  // us the button/touch is still held.
  const totalWidth = viewportWidth + maxPan
  const thumbWidthPct = totalWidth > 0 ? Math.max(8, (viewportWidth / totalWidth) * 100) : 100
  const thumbLeftPct = maxPan > 0 ? (panX / maxPan) * (100 - thumbWidthPct) : 0

  const panFromClientX = (clientX: number, track: HTMLElement) => {
    const rect = track.getBoundingClientRect()
    if (rect.width <= 0) return
    const clickPct = ((clientX - rect.left) / rect.width) * 100
    const usablePct = Math.max(1, 100 - thumbWidthPct)
    const targetLeftPct = Math.min(usablePct, Math.max(0, clickPct - thumbWidthPct / 2))
    setPanX(Math.min(maxPan, Math.max(0, (targetLeftPct / usablePct) * maxPan)))
  }

  const scrollBarElement = maxPan > 0 ? (
    <div
      role="scrollbar"
      aria-label="Scroll table horizontally"
      aria-orientation="horizontal"
      aria-valuemin={0}
      aria-valuemax={maxPan}
      aria-valuenow={Math.round(panX)}
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId)
        panFromClientX(e.clientX, e.currentTarget)
      }}
      onPointerMove={(e) => {
        if (e.buttons !== 1) return
        panFromClientX(e.clientX, e.currentTarget)
      }}
      className="relative h-2 w-full cursor-pointer rounded-full bg-muted transition-colors hover:bg-border"
    >
      <div
        className="absolute inset-y-0 rounded-full bg-subtle"
        style={{ width: `${thumbWidthPct}%`, left: `${thumbLeftPct}%` }}
      />
    </div>
  ) : null

  const toggleBtnClass = maximizable
    ? `shrink-0 rounded-[2px] p-1 transition-colors text-subtle hover:text-signal`
    : ''

  // One header bar, not two: the caller's own search/filter/count controls
  // (toolbar) as the left side, "..." and maximize/minimize pushed to the
  // rightmost edge of that same bar — rather than toolbar rendering as its
  // own row above a second row this table used to add just for those two
  // buttons.
  //
  // Sticky (in non-maximized mode) right below stickyOffset — the column
  // headers stick in turn below this bar's own measured height (see
  // stickyThTop above), so a caller's toolbar/action buttons stay visible
  // alongside the column headers while scrolling down, not just the headers
  // alone. z-30 keeps it above both the plain (z-10) and sticky-left (z-20)
  // header cells it sits on top of. Skipped in maximized mode: that overlay
  // already keeps topBar in view via normal flow (see the maximized branch
  // below), and stickyOffset refers to a caller bar outside the overlay.
  const topBar = (toolbar || onRefresh || maximizable) ? (
    <div
      ref={topBarRef}
      className={`px-4 py-3 border-b border-border bg-background flex items-center gap-2 ${stickyHeader && !maximized && !ownScroller ? 'sticky z-30' : ''}`}
      style={stickyHeader && !maximized && !ownScroller ? { top: stickyBase } : undefined}
    >
      {toolbar && <div className="flex-1 min-w-0 flex items-center gap-2">{toolbar}</div>}
      <div className="flex shrink-0 items-center gap-2 ml-auto">
        {maximizable && (
          <button
            type="button"
            onClick={() => setMaximized((v) => !v)}
            className={toggleBtnClass}
            aria-label={maximized ? 'Close' : 'Expand'}
          >
            {maximized ? <Minimize2 className="h-3 w-3" /> : <Maximize2 className="h-3 w-3" />}
          </button>
        )}
        {/* Always rightmost — the last thing in this bar. */}
        {onRefresh && <MoreMenu theme={theme} lastUpdated={lastUpdated} onRefresh={onRefresh} refreshing={refreshing} storageKey={storageKey} />}
      </div>
    </div>
  ) : null

  // Sticky to the *bottom* of the page's real scrolling ancestor (escaping
  // through the same overflow-clip card the sticky header does — see the
  // "Horizontal scroll failsafe" comment) rather than sitting in normal
  // flow above/below the table: a failsafe control that scrolls out of
  // view along with the table it controls defeats its own purpose the
  // moment the table is taller than the viewport. It only pins for as long
  // as this component's own wrapper (which includes the table) still
  // overlaps the visible viewport — once you've scrolled past the whole
  // table, it scrolls away with it normally. Scrollbar only now — "..."
  // and maximize/minimize live in topBar above instead.
  const controlBar = scrollBarElement ? (
    <div
      className={`sticky bottom-0 z-10 mt-1.5 flex items-center gap-3 border-t px-0.5 py-1.5 backdrop-blur-sm ${
        'border-border bg-card/95'
      }`}
    >
      <div className="flex-1">{scrollBarElement}</div>
    </div>
  ) : null

  if (!maximizable) {
    if (!controlBar && !topBar) return viewportElement
    return (
      <div>
        {topBar}
        {viewportElement}
        {controlBar}
      </div>
    )
  }

  // Maximized mode is a fixed full-viewport overlay, not sticky (there's no
  // outer page scroll happening while it's open — the overlay IS the whole
  // visible area, so an ordinary bottom row is already always visible).
  if (maximized) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-background p-4">
        {topBar}
        <div className="min-h-0 flex-1">
          {viewportElement}
        </div>
        {scrollBarElement && <div className="mt-2 shrink-0">{scrollBarElement}</div>}
      </div>
    )
  }

  return (
    <div>
      {topBar}
      {viewportElement}
      {controlBar}
    </div>
  )
}
